/**
 * What a repeated Identity Key inside one Feed means, as the two screens agree.
 *
 * The option list and the prefill rule, in a module with no React in it so both
 * can be tested — the control that renders them lives in
 * `components/FieldMapping.tsx` beside the role list, for the reason that file
 * states.
 *
 * **Why the prefill rule is written down rather than inlined.** It is the one
 * place a template's measurement reaches a form, and it has to answer two
 * questions at once: a template that declares nothing means `fail`, and a form
 * carrying `merge` across to a publisher nobody has measured would fold
 * collisions silently. Both are one-liners and both were got wrong by the
 * Record Path prefill this is modelled on, which left a stale value standing.
 */

import type { RepeatedIdentity } from "./api";

/** The server's default, and what a profile declaring nothing gets. */
export const REPEATED_IDENTITY_DEFAULT: RepeatedIdentity = "fail";

/**
 * The answers, in screen order.
 *
 * Phrased as what happens rather than as what the value is called. *Merge* says
 * nothing about which copy survives, and that is the whole of what is being
 * chosen: the first row stands and the rest are dropped, which is right for
 * identical copies and silently lossy for anything else.
 */
export const REPEATED_IDENTITY_OPTIONS: { value: RepeatedIdentity; label: string }[] = [
  { value: "fail", label: "Stop the Run — they are different players" },
  { value: "merge", label: "Keep the first — the publisher repeats rows" },
];

/** The hint under the control, which differs by answer because the risk does. */
export function repeatedIdentityHint(value: RepeatedIdentity): string {
  return value === "merge"
    ? "The first row stands and the repeats are dropped, so this is only right where the copies are identical. A publisher whose repeats differ loses every copy but the first, and the Run says nothing about it."
    : "Two rows under one Identity Key stop the Run, because a player ranked against a cohort containing himself is worse than no file. Change this only for a publisher measured repeating whole rows.";
}

/**
 * What a composed template seeds the control with.
 *
 * Cleared rather than left standing where a template declares nothing — see
 * the module docstring. Takes the `defaults` bag rather than the value so the
 * "declares nothing" case is this function's to answer rather than its
 * caller's, which is the half that gets forgotten.
 */
export function repeatedIdentityFor(defaults: {
  repeated_identity?: RepeatedIdentity;
}): RepeatedIdentity {
  return defaults.repeated_identity ?? REPEATED_IDENTITY_DEFAULT;
}
